/**
 * SME Reports Center — Two distinct documents + Carbon Pathways
 * 1. Climate Credentials Report (technical + credibility)
 * 2. Finance Brief (bank-facing decision document)
 */
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useJourney } from "@/context/JourneyContext";
import { FileText, Download, CheckCircle2, AlertCircle, ArrowRight, RefreshCw, GitBranch, Shield } from "lucide-react";
import { toast } from "sonner";

import { PageHeader, SectionCard, MetricCard, CTAButton } from "@/components/ds";
function ReportDownloadCard({ title, subtitle, description, endpoint, fileName, disabled, testId, accent }) {
  const [downloading, setDownloading] = useState(false);
  const { journeyData } = useJourney();
  const assessmentId = journeyData?.assessment_id;
  const org = journeyData?.organization;

  const handleDownload = async () => {
    if (!assessmentId) { toast.error("Complete your journey first."); return; }
    setDownloading(true);
    try {
      const token = localStorage.getItem("decarboniq_token");
      const url = endpoint(assessmentId);
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}${url}`,
        { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error(await res.text());
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = fileName(org?.name || "company");
      a.click();
      URL.revokeObjectURL(a.href);
      toast.success(`${title} downloaded.`);
    } catch (e) {
      console.error(e);
      toast.error("Download failed — please retry.");
    }
    setDownloading(false);
  };

  return (
    <div className={`bg-white border-2 rounded-xl p-6 ${disabled ? "opacity-60 border-slate-200" : accent ? "border-teal-200" : "border-slate-200"}`}
      data-testid={testId}>
      <div className="flex items-start gap-4 mb-5">
        <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${accent ? "bg-teal-50" : "bg-slate-50"}`}>
          <FileText size={20} className={accent ? "text-teal-600" : "text-slate-600"} />
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <h3 className="text-sm font-semibold text-slate-900">{title}</h3>
            {subtitle && <span className="text-xs font-bold text-slate-500 bg-slate-100 px-2 py-0.5 rounded-full">{subtitle}</span>}
            {!disabled && <span className="text-xs font-bold text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded-full flex items-center gap-0.5 flex-shrink-0"><CheckCircle2 size={8} /> Ready</span>}
          </div>
          <p className="text-xs text-slate-500 leading-relaxed">{description}</p>
        </div>
      </div>
      <button
        onClick={handleDownload}
        disabled={disabled || downloading}
        className={`w-full flex items-center justify-center gap-2 py-3 text-sm font-bold rounded-xl transition-colors disabled:opacity-50 ${
          accent ? "bg-[#0F172A] text-white hover:bg-slate-800" : "bg-slate-800 text-white hover:bg-slate-700"
        }`}
        data-testid={`download-${testId}`}>
        {downloading
          ? <><RefreshCw size={14} className="animate-spin" /> Generating...</>
          : <><Download size={14} /> Download {title}</>
        }
      </button>
    </div>
  );
}

export default function SMEReports() {
  const navigate = useNavigate();
  const { journeyData } = useJourney();

  const hasData     = !!(journeyData?.footprint?.total_tco2e);
  const assessmentId = journeyData?.assessment_id;
  const fp  = journeyData?.footprint;
  const crs = journeyData?.cars_score;

  return (
    <div className="p-6 lg:p-8 space-y-6">
      {/* Header */}
        
      <PageHeader
        label="Reports & Documents"
        title="Climate Reports"
        subtitle="Two distinct documents for two different audiences. Both generated from your verified data."
      />

      <div className="grid grid-cols-3 gap-4">
        <MetricCard label="Total Reports" value={reports.length} color="text-slate-900" />
      </div>

      {!hasData ? (
        <div className="max-w-2xl bg-amber-50 border border-amber-200 rounded-xl px-5 py-6 flex items-start gap-4">
          <AlertCircle size={20} className="text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm font-bold text-amber-800 mb-1">Reports unlock after you enter your data</p>
            <p className="text-xs text-amber-600 mb-4">Complete Step 2 of your journey to generate downloadable reports.</p>
            <button onClick={() => navigate("/sme-journey")}
              className="flex items-center gap-2 px-5 py-2.5 bg-amber-600 text-white text-sm font-bold rounded-xl hover:bg-amber-700">
              Enter Your Data <ArrowRight size={14} />
            </button>
          </div>
        </div>
      ) : (
        <SectionCard>
          <div className="max-w-3xl space-y-5">
          {/* Metrics strip */}
          <div className="grid grid-cols-3 gap-4">
            {[
              ["Emissions",   `${fp?.total_tco2e} tCO₂e/yr`, "text-slate-900"],
              ["CRS Score",   `${Math.round(crs?.score || 0)}/100 Band ${crs?.band || "—"}`, "text-teal-700"],
              ["Status",      "Ready for Financing Review", "text-emerald-700"],
            ].map(([l, v, c]) => (
              <div key={l} className="bg-white border border-slate-200 rounded-xl px-4 py-3">
                <p className="text-xs text-slate-400 uppercase tracking-wide mb-1">{l}</p>
                <p className={`text-xs font-bold ${c}`}>{v}</p>
              </div>
            ))}
          </div>

          {/* Report 1: Climate Credentials */}
          <ReportDownloadCard
            title="Climate Credentials Report"
            subtitle="Technical · Audit · Credibility"
            description="Complete technical record: emissions breakdown (Scope 1/2/3), GHG Protocol methodology, industry benchmark comparison, CRS score, audit trail, SDG alignment, and QR-verified unique report ID. For regulators, ESG teams, and DFI reviewers."
            endpoint={id => `/api/sme/climate-credentials/${id}`}
            fileName={name => `${name.replace(/[^a-zA-Z0-9]/g,"-")}-ClimateCredentials.pdf`}
            disabled={!hasData}
            testId="report-card-credentials"
            accent={true}
          />

          {/* Report 2: Finance Brief */}
          <ReportDownloadCard
            title="Finance Brief"
            subtitle="Bank-facing · Decision support"
            description="Concise decision document for lenders and investors: business overview, top 2 investment opportunities (capex/savings/payback), financial summary, CRS risk signal, and financing readiness indicator. Not a certification."
            endpoint={id => `/api/sme/finance-brief/${id}`}
            fileName={name => `${name.replace(/[^a-zA-Z0-9]/g,"-")}-FinanceBrief.pdf`}
            disabled={!hasData}
            testId="report-card-finance-brief"
            accent={false}
          />

          {/* Carbon Pathways CTA */}
          <button
            onClick={() => navigate("/carbon-pathways")}
            className="w-full flex items-center gap-3 px-5 py-4 border border-teal-200 text-teal-700 bg-teal-50 rounded-xl hover:bg-teal-100 transition-colors"
            data-testid="carbon-pathways-link">
            <GitBranch size={18} className="flex-shrink-0" />
            <div className="text-left flex-1">
              <p className="text-sm font-bold">Carbon Market Pathways</p>
              <p className="text-xs text-teal-600">Explore 4 structured pathways: Insetting · VCM · PoA · Article 6</p>
            </div>
            <ArrowRight size={14} className="flex-shrink-0" />
          </button>

          {/* Apply financing */}
          <button
            onClick={() => navigate("/sme/journey")}
            className="w-full flex items-center gap-3 px-5 py-3.5 border border-slate-200 text-slate-700 bg-white rounded-xl hover:bg-slate-50"
            data-testid="apply-financing-btn">
            <Shield size={16} className="text-slate-400 flex-shrink-0" />
            <div className="text-left flex-1">
              <p className="text-sm font-bold">Apply for Climate Financing</p>
              <p className="text-xs text-slate-500">Submit to DeCarbonIQ financing pipeline for lender review</p>
            </div>
            <ArrowRight size={14} className="flex-shrink-0 text-slate-400" />
          </button>

          <p className="text-xs text-slate-400 text-center">
            Reports generated from your latest calculation. Internally generated — not third-party certified.
          </p>
          </div>
        </SectionCard>
      )}
    </div>
  );
}
